import React, { useState, useEffect, useCallback } from "react";
import { Card, Space, Table, TableColumnsType, Row, Col } from "antd";
import { IntentService } from "../../services/IntentService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import { IntentGroup } from "../../model_gen/intent";
import { useNavigate } from "react-router-dom";

const IntentGroupsOverview: React.FC = () => {
    const navigate = useNavigate();
    const { apiKey, companyId } = usePermissions();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalIntentGroups, setTotalIntentGroups] = useState(0);
    const pageSize = 10;
    const [intentGroupsByPage, setIntentGroupsByPage] = useState<Record<number, IntentGroup[]>>({});

    const loadIntentGroups = useCallback(() => {
        if (!apiKey || !companyId) return;

        if (!intentGroupsByPage[currentPage]) {
            IntentService.getIntentGroups(
                (currentPage - 1) * pageSize,
                pageSize,
                setIsLoading,
                apiKey
            ).subscribe(
                (response: any) => {
                    setIntentGroupsByPage(prev => ({
                        ...prev,
                        [currentPage]: response.intent_groups
                    }));
                    setTotalIntentGroups(response.total);
                    setIsLoading(false);
                },
                (error: any) => {
                    console.error("Error loading intent groups:", error);
                    setIsLoading(false);
                }
            );
        }
    }, [apiKey, companyId, currentPage, pageSize]);

    useEffect(() => {
        loadIntentGroups();
    }, [loadIntentGroups]);

    const columns: TableColumnsType<IntentGroup> = [
        {
            title: "Intent Name",
            dataIndex: "intent_name",
            key: "intent_name",
            width: 300,
            sorter: (a, b) => a.intent_name.localeCompare(b.intent_name),
        },
        {
            title: "Date Created",
            dataIndex: "date_created",
            key: "date_created",
            width: 200,
            render: (text: number | null) => text ? moment(text).format("YYYY-MM-DD hh:mm:ss A") : "-",
            sorter: (a, b) => (a.date_created || 0) - (b.date_created || 0),
        },
        {
            title: "Last Activity",
            dataIndex: "last_activity",
            key: "last_activity",
            width: 200,
            render: (text: number | null) => {
                if (!text || text === 0) return "N/A";
                return moment(text).format("YYYY-MM-DD hh:mm:ss A");
            },
            sorter: (a, b) => (a.last_activity || 0) - (b.last_activity || 0),
        },
        {
            title: "# Requests",
            dataIndex: "request_count",
            key: "request_count",
            width: 150,
            align: "right" as const,
            render: (text: number) => text.toLocaleString(),
            sorter: (a, b) => (a.request_count || 0) - (b.request_count || 0),
        }
    ];

    return (
        <Card className="applications-wrapper">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <h1>Intent Groups</h1>
                    </Col>
                </Row>
                {isLoading ? (
                    <div style={{ width: "100%" }}>
                        <Table
                            rowKey={(record) => record.id?.toString() || record.intent_name}
                            columns={columns}
                            dataSource={[]}
                            loading={true}
                            pagination={false}
                            style={{ width: "100%" }}
                        />
                    </div>
                ) : (
                    <Table
                        rowKey={(record) => record.id?.toString() || record.intent_name}
                        columns={columns}
                        dataSource={intentGroupsByPage[currentPage] || []}
                        onRow={(record) => ({
                            onClick: () => navigate(`/intent-group/${record.id}`),
                            style: { cursor: "pointer" }
                        })}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalIntentGroups,
                            onChange: (page) => setCurrentPage(page),
                            showSizeChanger: false
                        }}
                        scroll={{ x: 850 }}
                        style={{ width: "100%" }}
                    />
                )}
            </Space>
        </Card>
    );
};

export default IntentGroupsOverview;